import { VoiceAIConfig } from '../client';
import { STTOptions, STTResult } from '../stt';
import { makeRequest } from '../utils/request';
import FormData from 'form-data';

export interface STTProvider {
  transcribe: (audio: File | Blob | ArrayBuffer | string, options: STTOptions, config: VoiceAIConfig) => Promise<STTResult>;
  languages?: string[];
}

export const STTProviders: Record<string, STTProvider> = {
  'whisper-v3': {
    transcribe: async (audio, options, config) => {
      const formData = new FormData();
      
      if (audio instanceof File) {
        formData.append('audio', audio, audio.name);
      } else if (audio instanceof Blob) {
        formData.append('audio', audio, 'audio.wav');
      } else if (audio instanceof ArrayBuffer) {
        const blob = new Blob([audio]);
        formData.append('audio', blob, 'audio.wav');
      } else if (typeof audio === 'string') {
        formData.append('audio', audio);
      }

      if (options.language) {
        formData.append('language', options.language);
      }

      const response = await makeRequest({
        url: '/v1/stt/whisper-v3',
        method: 'POST',
        data: formData,
        headers: formData.getHeaders(),
        responseType: 'json',
        config,
      });

      return {
        text: response.text,
        confidence: response.confidence,
        language: response.language || options.language,
        segments: response.segments,
      };
    },
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko', 'ar', 'hi'],
  },

  'kyutai': {
    transcribe: async (audio, options, config) => {
      const formData = new FormData();
      
      if (audio instanceof File) {
        formData.append('audio', audio, audio.name);
      } else if (audio instanceof Blob) {
        formData.append('audio', audio, 'audio.wav');
      } else if (audio instanceof ArrayBuffer) {
        const blob = new Blob([audio]);
        formData.append('audio', blob, 'audio.wav');
      } else if (typeof audio === 'string') {
        formData.append('audio', audio);
      }

      if (options.language) {
        if (!['en', 'fr'].includes(options.language)) {
          throw new Error('Kyutai only supports English (en) and French (fr)');
        }
        formData.append('language', options.language);
      }

      if (options.timestamps !== undefined) {
        formData.append('timestamps', String(options.timestamps));
      }

      const response = await makeRequest({
        url: '/v1/stt/in/kyutai',
        method: 'POST',
        data: formData,
        headers: formData.getHeaders(),
        responseType: 'json',
        config,
      });

      return {
        text: response.text,
        confidence: response.confidence,
        language: response.language || options.language,
        segments: response.segments,
      };
    },
    languages: ['en', 'fr'],
  },

  'general': {
    transcribe: async (audio, options, config) => {
      const formData = new FormData();
      
      if (audio instanceof File) {
        formData.append('audio', audio, audio.name);
      } else if (audio instanceof Blob) {
        formData.append('audio', audio, 'audio.wav');
      } else if (audio instanceof ArrayBuffer) {
        const blob = new Blob([audio]);
        formData.append('audio', blob, 'audio.wav');
      } else if (typeof audio === 'string') {
        formData.append('audio', audio);
      }

      if (options.language) {
        formData.append('config', JSON.stringify({ language: options.language }));
      }

      const response = await makeRequest({
        url: '/v1/stt',
        method: 'POST',
        data: formData,
        headers: formData.getHeaders(),
        responseType: 'json',
        config,
      });

      return {
        text: response.text,
        confidence: response.confidence,
        language: response.language || options.language,
      };
    },
    languages: ['en'],
  },
};